import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import MaskedInput from 'react-text-mask';
import emailMask from 'text-mask-addons/dist/emailMask';
import { useTheme } from '@mui/material/styles';
import MUITextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { countries } from '../../utils/primaryData';
import Visibility from '@mui/icons-material/Visibility';
import {
  CheckCircle,
  Comment,
  HelpOutlineOutlined,
  InfoOutlined,
  Search,
  Send,
  VisibilityOff,
  VpnKeyOutlined,
} from '@mui/icons-material';
import { CircularProgress, Tooltip } from '@mui/material';
import { toast } from 'react-toastify';
import nodeAxiosFirebase from '../../utils/nodeAxiosFirebase';
import { setNodeAssistant } from '../../redux/actions-v2/coreAction';

const TextMaskCustom = (props) => {
  const { inputRef, mask, ...other } = props;
  const handleClick = (event) => {
    event.target.setSelectionRange(0, 0);
  };
  const emailMaskDefault = [
    /[a-zA-Z0-9._%+-]/,
    /[a-zA-Z0-9._%+-]/,
    /[a-zA-Z0-9._%+-]/,
    /[a-zA-Z0-9._%+-]/,
    /[a-zA-Z0-9._%+-]/,
    /[a-zA-Z0-9._%+-]/,
    /[a-zA-Z0-9._%+-]/,
    /[a-zA-Z0-9._%+-]/,
    /[a-zA-Z0-9._%+-]/,
    /[a-zA-Z0-9._%+-]/,
    '@',
    /[a-zA-Z0-9.-]/,
    /[a-zA-Z0-9.-]/,
    /[a-zA-Z0-9.-]/,
    '.',
    /[a-zA-Z]/,
    /[a-zA-Z]/,
  ];

  return (
    <MaskedInput
      {...other}
      ref={(ref) => {
        if (typeof inputRef === 'function') {
          inputRef(ref ? ref.inputElement : null);
        }
      }}
      mask={mask === emailMaskDefault ? emailMask : mask}
      onClick={handleClick}
      placeholderChar={'\u2000'}
      showMask
    />
  );
};



export const TextField = ({
  variant = 'outlined',
  onChange,
  onBlur,
  type,
  multiline,
  mask,
  value,
  valueSer,
  size,
  keyStructure,
  help,
  validation,
  toReset,
  isLoading,
  transform,
  dontAllowSlash,
  staticField,
  onChangeServer,
  fieldType,
  handleKeyPress,
  ref,
  ...props
}) => {
  const businessPreference = useSelector((state) => state.core.businessData);

  const theme = useTheme();
  const dispatch = useDispatch();
  const isDarkmode = theme.palette.mode === 'dark';

  const { t } = useTranslation();
  let maskArray = mask;

  if (typeof mask === 'string') {
    let maskString = mask || '';
    maskArray = maskString?.slice(1, -1)?.split(', ');

    maskArray = maskArray?.map((item) => {
      if (item?.startsWith('/') && item?.endsWith('/')) {
        return new RegExp(item?.slice(1, -1));
      }
      if (item?.startsWith("'") && item?.endsWith("'")) {
        return item.slice(1, -1);
      }
      return item;
    });
  }

  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isValidated, setIsValidate] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('ca');
  const [phoneNumber, setPhoneNumber] = useState('');

  const handleCopy = () => {
    let finalHelp = help?.startsWith('ID') ? help?.split('ID: ')[1] : help;
    navigator.clipboard.writeText(finalHelp);
    toast.success(t('copied'));
  };

  const onChangeField = (event) => {
    let newEvent = event;

    if (
      event?.target?.value?.includes('/') &&
      dontAllowSlash &&
      (fieldType === 'text' || fieldType === 'string')
    ) {
      newEvent = {
        target: {
          value: event.target.value.replace(/\//g, ''),
        },
      };
      setError('/');
    } else {
      setError('');
    }

    onChange(newEvent, fieldType, null, null);
  };

  const checkData = async (keyTrans, collection, type = 'check') => {
    try {
      if (
        businessPreference?.formula?.toUpperCase() === 'RISE' ||
        businessPreference?.formula?.toUpperCase() === 'TRIAL'
      ) {
        const response = await nodeAxiosFirebase({
          t,
          method: 'POST',
          url: `business/check`,
          body: {
            key: keyTrans,
            collection: collection,
            value: value,
            type: type
          },
        });

        if (response?.validation === 'invalid') {
          dispatch(
            setNodeAssistant({
              text: t('notValidDomain'),
              color: '#9b111e',
            })
          );
        } else if (response?.validation === true) {
          dispatch(
            setNodeAssistant({
              text: t('ohMayAlreadyExist' + keyTrans),
              color: '#9b111e',
            })
          );
        } else {
          dispatch(setNodeAssistant({ text: '', color: null }));
        }
      }
    } catch (error) {
      console.error('Error getting data', error);
      return true;
    }
  };

  const handleBlur = async (event) => {
    if (onBlur) {
      onBlur(event, fieldType);
    }
    if ((type === 'email' || keyStructure === 'targetEmail') && value) {

      if (keyStructure === 'targetEmail') {
        await checkData('targetEmail', 'connections', 'validate');
      }
    } else if (keyStructure === 'sku' && value) {
      await checkData('sku', 'articles');
    } else {
      if (keyStructure === 'targetPhone' && value)
        await checkData('targetPhone', 'connections');
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const transformValue = (value) => {
    if (transform === 'uppercase') {
      return value.toUpperCase();
    } else if (transform === 'lowercase') {
      return value.toLowerCase();
    } else if (transform === 'capitalize') {
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
    return value;
  };

  useEffect(() => {
    if (validation !== 'none' && validation !== null) {
      try {
        // Ensure validation is a valid regex string
        const regex = new RegExp(validation);
        const isValid = regex.test(value);
        setIsValidate(isValid);
      } catch (e) {
        console.error('Invalid regex:', validation, e);
        setIsValidate(false);
      }
    }
    if (transform && value) {
      const transformedValue = transformValue(value);
      onChange({ target: { value: transformedValue } }, fieldType, null);
    }
  }, [value]);

  useEffect(() => {
    if (value) {
      // Extract country code from the value if it exists
      const stringValue = String(value);
      const country = countries?.find(c => stringValue?.startsWith(c?.dialCode));
      if (country) {
        setSelectedCountry(country?.code);
        setPhoneNumber(stringValue?.replace(country?.dialCode, ''));
      } else {
        setPhoneNumber(stringValue);
      }
    }
  }, [value]);

  let transformedText = '';

  if (validation) {
    transformedText = validation
      .replace(/\\d\{(\d+)\}/g, (_, n) => '#'.repeat(n))
      .replace(/[\^$\\]/g, '');
  }

  const formatPhoneNumber = (number) => {
    if (!number) return '';
    const cleaned = number.replace(/\D/g, '');
    // Remove country code for display
    const country = countries.find(c => c.code === selectedCountry);
    const countryCode = country?.dialCode.replace('+', '') || '';
    const numberWithoutCountryCode = cleaned.startsWith(countryCode)
      ? cleaned.slice(countryCode.length)
      : cleaned;

    if (numberWithoutCountryCode.length <= 3) return numberWithoutCountryCode;
    if (numberWithoutCountryCode.length <= 6) return `(${numberWithoutCountryCode.slice(0, 3)}) ${numberWithoutCountryCode.slice(3)}`;
    return `(${numberWithoutCountryCode.slice(0, 3)}) ${numberWithoutCountryCode.slice(3, 6)}-${numberWithoutCountryCode.slice(6, 10)}`;
  };

  const handlePhoneChange = (event) => {
    const value = event.target.value;
    const cleaned = value.replace(/\D/g, '');
    setPhoneNumber(cleaned);
    // Just pass the cleaned number without country code during typing
    onChange({ target: { value: cleaned } }, fieldType, null, null);
  };

  const handlePhoneBlur = (event) => {
    const cleaned = event.target.value.replace(/\D/g, '');
    const country = countries.find(c => c.code === selectedCountry);
    if (country) {
      const countryCode = country.dialCode.replace('+', '');
      // Only add country code if it's not already there
      const formattedNumber = cleaned.startsWith(countryCode)
        ? cleaned
        : countryCode + cleaned;

      // Update the display value with formatting (without country code)
      setPhoneNumber(formattedNumber);

      // Send the raw number with country code to the backend
      onChange({ target: { value: formattedNumber } }, fieldType, null, null);

      if (onBlur) {
        onBlur({ target: { value: formattedNumber } }, fieldType);
      }
    }
  };

  const handleCountryChange = (event) => {
    const countryCode = event.target.value;
    setSelectedCountry(countryCode);
    const country = countries.find(c => c.code === countryCode);
    if (country && phoneNumber) {
      // Remove any existing country code from the current number
      const currentCountry = countries.find(c => c.code === selectedCountry);
      const currentCountryCode = currentCountry?.dialCode.replace('+', '') || '';
      const numberWithoutCountryCode = phoneNumber.startsWith(currentCountryCode)
        ? phoneNumber.slice(currentCountryCode.length)
        : phoneNumber;

      // Add the new country code
      const newCountryCode = country.dialCode.replace('+', '');
      const formattedNumber = newCountryCode + numberWithoutCountryCode;

      // Update both the display and backend value
      setPhoneNumber(formattedNumber);
      onChange({ target: { value: formattedNumber } }, fieldType, null, null);

      // Trigger onBlur to save the changes
      if (onBlur) {
        onBlur({ target: { value: formattedNumber } }, fieldType);
      }
    }
  };

  return (
    <div>
      {type === 'phone' ? (
        <MUITextField
          variant={variant}
          {...props}
          ref={ref}
          InputLabelProps={{
            shrink: true,
          }}
          onChange={handlePhoneChange}
          onBlur={onBlur && handlePhoneBlur}
          margin="normal"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleKeyPress && handleKeyPress(e);
            }
          }}
          type="text"
          error={props.error}
          helperText={
            <span className="fs-10" onClick={handleCopy}>
              {help}
            </span>
          }
          value={staticField ? '' : formatPhoneNumber(phoneNumber)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Select
                  value={selectedCountry}
                  onChange={handleCountryChange}
                  size={size}
                  sx={{
                    minWidth: '80px',
                    backgroundColor: isDarkmode ? 'rgb(51,51,51)' : 'white',
                    color: isDarkmode ? 'white' : 'black',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                      display: 'none',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                      display: 'none',
                    },
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 14px',
                      height: '25px',
                      minHeight: '25px',
                      maxHeight: '25px',
                      border: 'none',
                      '&:focus': {
                        border: 'none',
                        backgroundColor: 'transparent',
                      },
                    },
                    '& .MuiOutlinedInput-root': {
                      height: '25px',
                      minHeight: '25px',
                      maxHeight: '25px',
                      border: 'none',
                      '& fieldset': {
                        border: 'none',
                        display: 'none',
                      },
                      '&:hover fieldset': {
                        border: 'none',
                        display: 'none',
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none',
                        display: 'none',
                      },
                    },
                    '& .MuiSelect-icon': {
                      color: isDarkmode ? 'white' : 'black',
                    },
                  }}
                  renderValue={(value) => {
                    const country = countries.find(c => c.code === value);
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '20px' }}>{country?.flag}</span>
                        <span>{country?.dialCode}</span>
                      </div>
                    );
                  }}
                >
                  {countries.map((country) => (
                    <MenuItem key={country.code} value={country.code}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '20px' }}>{country.flag}</span>
                        <span>{country.dialCode}</span>
                      </div>
                    </MenuItem>
                  ))}
                </Select>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiFormLabel-root': {
              backgroundColor: isDarkmode && 'rgb(51,51,51)',
              marginTop: size === 'small' ? '18px' : '',
            },
            '& .MuiOutlinedInput-root': {
              marginTop: size === 'small' ? '18px' : '',
              height: '25px',
              maxHeight: '25px',
              minHeight: '25px',
              '&.Mui-focused fieldset': {
                borderColor: businessPreference?.mainColor || '#000',
                boxShadow: `0 0 0 0.2rem ${businessPreference?.mainColor + '20'}`,
              },
            },
          }}
        />
      ) : type === 'password' ? (
        <MUITextField
          variant={variant}
          {...props}
          ref={ref}
          InputLabelProps={{
            shrink: true,
          }}
          onChange={(event) => onChangeField(event, fieldType)}
          onBlur={onBlur && ((event) => onBlur(event, fieldType))}
          margin="normal"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleKeyPress && handleKeyPress(e);
            }
          }}
          type={showPassword ? 'text' : 'password'}
          error={props.error}
          helperText={
            <span className="fs-10" onClick={handleCopy}>
              {help}
            </span>
          }
          value={staticField ? '' : value}
          InputProps={{
            endAdornment: (
              <InputAdornment sx={{ marginRight: '-4px' }} position="end">
                {showPassword ? (
                  <VisibilityOff
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                  />
                ) : (
                  <Visibility
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                  />
                )}
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiFormLabel-root': {
              backgroundColor: isDarkmode && 'rgb(51,51,51)',
              marginTop: size === 'small' ? '18px' : '',
            },
            '& .MuiOutlinedInput-root': {
              marginTop: size === 'small' ? '18px' : '',
              height: '25px',
              maxHeight: '25px',
              minHeight: '25px',
              '&.Mui-focused fieldset': {
                borderColor: businessPreference?.mainColor || '#000',
                boxShadow: `0 0 0 0.2rem ${businessPreference?.mainColor + '20'
                  }`,
              },
            },

          }}
        />
      ) : type === 'codeAccess' ? (
        <MUITextField
          variant={variant}
          {...props}
          ref={ref}
          InputLabelProps={{
            shrink: true,
          }}
          onChange={(event) => onChangeField(event, fieldType)}
          onBlur={onBlur && ((event) => onBlur(event, fieldType))}
          margin="normal"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleKeyPress && handleKeyPress(e);
            }
          }}
          type={'text'}
          error={props.error}
          helperText={
            <span className="fs-10" onClick={handleCopy}>
              {help}
            </span>
          }
          value={staticField ? '' : value}
          InputProps={{
            endAdornment: (
              <InputAdornment sx={{ marginRight: '-4px' }} position="end">
                <VpnKeyOutlined />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiFormLabel-root': {
              backgroundColor: isDarkmode && 'rgb(51,51,51)',
              marginTop: size === 'small' ? '18px' : '',
            },
            '& .MuiOutlinedInput-root': {
              marginTop: size === 'small' ? '18px' : '',
              height: '25px',
              maxHeight: '25px',
              minHeight: '25px',
              '&.Mui-focused fieldset': {
                borderColor: businessPreference?.mainColor || '#000',
                boxShadow: `0 0 0 0.2rem ${businessPreference?.mainColor + '20'
                  }`,
              },
            },

          }}
        />
      ) : validation !== null &&
        validation !== 'none' &&
        validation !== '' &&
        (fieldType === 'text' || fieldType === 'string') ? (
        <MUITextField
          variant={variant}
          {...props}
          ref={ref}
          InputLabelProps={{
            shrink: true,
          }}
          onChange={(event) => onChangeField(event, fieldType)}
          onBlur={(event) => handleBlur(event, fieldType)}
          margin="normal"
          type={'text'}
          multiline={multiline || false}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleKeyPress && handleKeyPress(e);
            }
          }}
          error={props?.error || error === '/'}
          helperText={
            <span className="fs-10" onClick={handleCopy}>
              {help}
            </span>
          }
          value={staticField ? '' : value}
          InputProps={{
            inputComponent: mask && TextMaskCustom,
            inputProps: { mask: mask && maskArray },
            endAdornment: (
              <InputAdornment sx={{ marginRight: '-4px' }} position="end">
                {isValidated ? (
                  <CheckCircle color="success" />
                ) : (
                  <Tooltip title={transformedText}>
                    <InfoOutlined color="error" />
                  </Tooltip>
                )}
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiFormLabel-root': {
              backgroundColor: isDarkmode && 'rgb(51,51,51)',
              marginTop: size === 'small' ? '18px' : '',
            },
            '& .MuiOutlinedInput-root': {
              marginTop: size === 'small' ? '18px' : '',
              height: '25px',
              maxHeight: '25px',
              minHeight: '25px',
              '&.Mui-focused fieldset': {
                borderColor: businessPreference?.mainColor || '#000',
                boxShadow: `0 0 0 0.2rem ${businessPreference?.mainColor + '20'
                  }`,
              },
            },

          }}
        />
      ) : (
        <MUITextField
          variant={variant}
          {...props}
          ref={ref}
          InputLabelProps={{
            shrink: true,
          }}
          onChange={(event) => onChangeField(event, fieldType)}
          onBlur={(event) => handleBlur(event, fieldType)}
          margin="normal"
          type={type}
          error={props.error}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleKeyPress(e);
            }
          }}
          helperText={
            <span className="fs-10" onClick={handleCopy}>
              {help}
            </span>
          }
          value={staticField ? '' : value}
          InputProps={{
            inputComponent: mask && TextMaskCustom,
            inputProps: { mask: mask && maskArray, },
            endAdornment: (
              <InputAdornment sx={{ marginRight: '-4px' }} onClick={handleCopy} position="end">
                {isLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : type === 'search' ? (
                  <Search />
                ) : type === 'comment' ? (
                  <Comment />
                ) : type === 'message' ? (
                  <Send />
                ) : (
                  <>
                    {help && <span style={{ cursor: 'default' }}><Tooltip title={help}><HelpOutlineOutlined /></Tooltip></span>}
                  </>
                )}
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiFormLabel-root': {
              backgroundColor: isDarkmode && 'rgb(51,51,51)',
              marginTop: size === 'small' ? '18px' : '',
            },
            '& .MuiOutlinedInput-root': {
              marginTop: size === 'small' ? '18px' : '',
              height: '25px',
              maxHeight: '25px',
              minHeight: '25px',
              '&.Mui-focused fieldset': {
                borderColor: businessPreference?.mainColor || '#000',
                boxShadow: `0 0 0 0.2rem ${businessPreference?.mainColor + '20'}`,
              },
            },
          }}
        />
      )}
    </div>
  );
};


export default TextField;